import { useMemo } from "react";
import { useWeeklyOvertimeHours } from "@/entities/overtime-watch";
import type { DayOfWeek } from "@/entities/shift/model/types";

export type GuardOtHint = {
  status: "over" | "near" | null;
  projectedHours: number;
  afterHours: number;
  thresholdHours: number;
};

export function useGuardOvertimeHints(args: {
  startDate?: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  excludeShiftId?: number;
  recurrenceDays?: DayOfWeek[];
}): Map<number, GuardOtHint> {
  const { startDate, startTime, endDate, endTime, excludeShiftId, recurrenceDays } = args;

  const occurrenceHours = useMemo(() => {
    if (!startDate || !startTime || !endDate || !endTime) return 0;
    const s = new Date(`${startDate}T${startTime}`);
    const e = new Date(`${endDate}T${endTime}`);
    const h = (e.getTime() - s.getTime()) / 3_600_000;
    return Number.isFinite(h) && h > 0 ? h : 0;
  }, [startDate, startTime, endDate, endTime]);

  // Advisory approximation: treats recurrence as occurrences-per-week. Backend recompute is authoritative.
  const occurrencesPerWeek = Math.max(1, recurrenceDays?.length ?? 0);
  const shiftHours = occurrenceHours * occurrencesPerWeek;

  const { data } = useWeeklyOvertimeHours(
    startDate ? { weekOf: startDate, excludeShiftId } : undefined
  );

  return useMemo(() => {
    const map = new Map<number, GuardOtHint>();
    if (!data) return map;
    const { thresholdHours, nearLimitHours } = data;
    for (const g of data.guards) {
      const after = g.projectedHours + shiftHours;
      const status =
        after > thresholdHours ? "over" : after >= thresholdHours - nearLimitHours ? "near" : null;
      map.set(g.guardUserId, { status, projectedHours: g.projectedHours, afterHours: after, thresholdHours });
    }
    return map;
  }, [data, shiftHours]);
}
